<template>
  <div class="dashboard-container">
    <h1>Аналитическая панель</h1>
    
    <div class="date-filter">
      <div class="filter-group">
        <label for="dateRange">Период:</label>
        <select id="dateRange" v-model="dateRange" @change="applyDateFilter">
          <option value="all">Всё время</option>
          <option value="today">Сегодня</option>
          <option value="yesterday">Вчера</option>
          <option value="week">Последние 7 дней</option>
          <option value="month">Последние 30 дней</option>
          <option value="custom">Произвольный период</option>
        </select>
      </div>
      
      <div v-if="dateRange === 'custom'" class="custom-date-range">
        <div class="filter-group">
          <label for="startDate">С:</label>
          <input type="date" id="startDate" v-model="startDate" @change="applyDateFilter">
        </div>
        <div class="filter-group">
          <label for="endDate">По:</label>
          <input type="date" id="endDate" v-model="endDate" @change="applyDateFilter">
        </div>
      </div>
    </div>
    
    <div class="postback-url-section">
      <h2>URL для постбэков</h2>
      <div class="postback-url-container">
        <input type="text" readonly :value="postbackUrl" class="postback-url-input">
        <button @click="copyPostbackUrl" class="copy-button">
          <i class="fas fa-copy"></i> Копировать
        </button>
      </div>
      <div class="postback-info">
        <p>Используйте этот URL для настройки постбэков в партнерской программе.</p>
        <p>Поддерживаемые методы: GET и POST</p>
      </div>
    </div>
    
    <div class="stats-cards">
      <div class="stats-card">
        <div class="stats-card-title">Регистрации</div>
        <div class="stats-card-value">{{ filteredStats.total.registrations }}</div>
      </div>
      
      <div class="stats-card">
        <div class="stats-card-title">Подтверждения</div>
        <div class="stats-card-value">{{ filteredStats.total.confirmations }}</div>
      </div>
      
      <div class="stats-card">
        <div class="stats-card-title">Первые депозиты</div>
        <div class="stats-card-value">{{ filteredStats.total.firstDeposits }}</div>
      </div>
      
      <div class="stats-card">
        <div class="stats-card-title">Все депозиты</div>
        <div class="stats-card-value">{{ filteredStats.total.deposits }}</div>
      </div>
      
      <div class="stats-card">
        <div class="stats-card-title">Сумма депозитов</div>
        <div class="stats-card-value">{{ formatCurrency(filteredStats.total.depositAmount) }}</div>
      </div>
    </div>
    
    <div class="campaigns-section">
      <h2>Статистика по кампаниям</h2>
      
      <div class="campaigns-table-container">
        <table class="campaigns-table">
          <thead>
            <tr>
              <th>Кампания</th>
              <th>Регистрации</th>
              <th>Подтверждения</th>
              <th>Первые депозиты</th>
              <th>Все депозиты</th>
              <th>Сумма депозитов</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="campaign in filteredStats.campaigns" :key="campaign._id">
              <td>{{ campaign._id || 'Без кампании' }}</td>
              <td>{{ campaign.registrations }}</td>
              <td>{{ campaign.confirmations }}</td>
              <td>{{ campaign.firstDeposits }}</td>
              <td>{{ campaign.deposits }}</td>
              <td>{{ formatCurrency(campaign.depositAmount) }}</td>
              <td>
                <button @click="viewCampaignDetails(campaign._id)" class="view-details-button">
                  <i class="fas fa-eye"></i> Детали
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <div v-if="selectedCampaign" class="campaign-details">
      <div class="campaign-details-header">
        <h2>Детали кампании: {{ selectedCampaign }}</h2>
        <button @click="selectedCampaign = null" class="close-button">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="postbacks-table-container">
        <table class="postbacks-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Тип события</th>
              <th>ID трейдера</th>
              <th>Сумма</th>
              <th>Click ID</th>
              <th>Site ID</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="postback in filteredCampaignPostbacks" :key="postback._id">
              <td>{{ formatDate(postback.timestamp) }}</td>
              <td>{{ getEventType(postback) }}</td>
              <td>{{ postback.trader_id }}</td>
              <td>{{ postback.sumdep ? formatCurrency(postback.sumdep) : '-' }}</td>
              <td>{{ postback.click_id }}</td>
              <td>{{ postback.site_id }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import axios from 'axios';
import socket from '../plugins/socket';

export default {
  name: 'DashboardView',
  setup() {
    const stats = ref({
      total: {
        registrations: 0,
        confirmations: 0,
        firstDeposits: 0,
        deposits: 0,
        depositAmount: 0
      },
      campaigns: []
    });
    
    const allPostbacks = ref([]);
    const campaignPostbacks = ref([]);
    const selectedCampaign = ref(null);
    const loading = ref(false);
    const error = ref(null);
    
    // Настройки фильтра по дате
    const dateRange = ref('all');
    const startDate = ref(getDateString(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))); // 30 дней назад
    const endDate = ref(getDateString(new Date())); // сегодня
    
    // URL для постбэков
    const postbackUrl = computed(() => {
      const baseUrl = window.location.origin;
      return `${baseUrl}/postbacks/receive`;
    });
    
    // Фильтрованные данные
    const filteredStats = computed(() => {
      if (dateRange.value === 'all') {
        return stats.value;
      }
      
      // Фильтруем постбэки по дате
      const filteredPostbacks = filterPostbacksByDate(allPostbacks.value);
      
      // Рассчитываем общую статистику
      const totalRegistrations = filteredPostbacks.filter(p => p.reg).length;
      const totalConfirmations = filteredPostbacks.filter(p => p.conf).length;
      const totalFirstDeposits = filteredPostbacks.filter(p => p.ftd).length;
      const totalDeposits = filteredPostbacks.filter(p => p.dep).length;
      
      // Рассчитываем сумму депозитов
      const totalDepositAmount = filteredPostbacks
        .filter(p => p.ftd || p.dep)
        .reduce((sum, p) => sum + (p.sumdep || 0), 0);
      
      // Группируем по кампаниям
      const campaignMap = {};
      
      filteredPostbacks.forEach(postback => {
        const campaignId = postback.ac || 'unknown';
        
        if (!campaignMap[campaignId]) {
          campaignMap[campaignId] = {
            _id: campaignId,
            registrations: 0,
            confirmations: 0,
            firstDeposits: 0,
            deposits: 0,
            depositAmount: 0
          };
        }
        
        if (postback.reg) campaignMap[campaignId].registrations++;
        if (postback.conf) campaignMap[campaignId].confirmations++;
        if (postback.ftd) {
          campaignMap[campaignId].firstDeposits++;
          campaignMap[campaignId].depositAmount += (postback.sumdep || 0);
        }
        if (postback.dep) {
          campaignMap[campaignId].deposits++;
          campaignMap[campaignId].depositAmount += (postback.sumdep || 0);
        }
      });
      
      const campaignStats = Object.values(campaignMap);
      
      return {
        total: {
          registrations: totalRegistrations,
          confirmations: totalConfirmations,
          firstDeposits: totalFirstDeposits,
          deposits: totalDeposits,
          depositAmount: totalDepositAmount
        },
        campaigns: campaignStats
      };
    });
    
    // Фильтрованные постбэки для выбранной кампании
    const filteredCampaignPostbacks = computed(() => {
      if (dateRange.value === 'all') {
        return campaignPostbacks.value;
      }
      
      return filterPostbacksByDate(campaignPostbacks.value);
    });
    
    // Функция для фильтрации постбэков по дате
    function filterPostbacksByDate(postbacks) {
      let startDateTime, endDateTime;
      
      switch (dateRange.value) {
        case 'today':
          startDateTime = new Date();
          startDateTime.setHours(0, 0, 0, 0);
          endDateTime = new Date();
          endDateTime.setHours(23, 59, 59, 999);
          break;
          
        case 'yesterday':
          startDateTime = new Date();
          startDateTime.setDate(startDateTime.getDate() - 1);
          startDateTime.setHours(0, 0, 0, 0);
          endDateTime = new Date();
          endDateTime.setDate(endDateTime.getDate() - 1);
          endDateTime.setHours(23, 59, 59, 999);
          break;
          
        case 'week':
          startDateTime = new Date();
          startDateTime.setDate(startDateTime.getDate() - 7);
          startDateTime.setHours(0, 0, 0, 0);
          endDateTime = new Date();
          endDateTime.setHours(23, 59, 59, 999);
          break;
          
        case 'month':
          startDateTime = new Date();
          startDateTime.setDate(startDateTime.getDate() - 30);
          startDateTime.setHours(0, 0, 0, 0);
          endDateTime = new Date();
          endDateTime.setHours(23, 59, 59, 999);
          break;
          
        case 'custom':
          startDateTime = new Date(startDate.value);
          startDateTime.setHours(0, 0, 0, 0);
          endDateTime = new Date(endDate.value);
          endDateTime.setHours(23, 59, 59, 999);
          break;
          
        default:
          return postbacks;
      }
      
      return postbacks.filter(postback => {
        const postbackDate = new Date(postback.timestamp);
        return postbackDate >= startDateTime && postbackDate <= endDateTime;
      });
    }
    
    // Применение фильтра по дате
    function applyDateFilter() {
      console.log('Применяем фильтр по дате:', dateRange.value);
      
      if (selectedCampaign.value) {
        fetchCampaignPostbacks(selectedCampaign.value);
      }
    }
    
    // Получение строки даты в формате YYYY-MM-DD
    function getDateString(date) {
      return date.toISOString().split('T')[0];
    }
    
    // Копирование URL для постбэков
    const copyPostbackUrl = () => {
      navigator.clipboard.writeText(postbackUrl.value)
        .then(() => {
          alert('URL скопирован в буфер обмена');
        })
        .catch(err => {
          console.error('Ошибка при копировании URL:', err);
          alert('Не удалось скопировать URL. Пожалуйста, скопируйте его вручную.');
        });
    };
    
    // Получение статистики
    const fetchStats = async () => {
      try {
        loading.value = true;
        error.value = null;
        
        const [statsResponse, postbacksResponse] = await Promise.all([
          axios.get('/postbacks/stats'),
          axios.get('/postbacks')
        ]);
        
        stats.value = statsResponse.data;
        allPostbacks.value = postbacksResponse.data;
        
        console.log('Получена статистика:', stats.value);
        console.log('Получено постбэков:', allPostbacks.value.length);
      } catch (err) {
        console.error('Ошибка при получении статистики:', err);
        error.value = 'Ошибка при получении статистики';
      } finally {
        loading.value = false;
      }
    };
    
    // Получение постбэков для конкретной кампании
    const fetchCampaignPostbacks = async (campaignId) => {
      try {
        loading.value = true;
        error.value = null;
        
        const response = await axios.get(`/postbacks/campaign/${campaignId}`);
        campaignPostbacks.value = response.data;
        
        console.log(`Получено ${campaignPostbacks.value.length} постбэков для кампании ${campaignId}`);
      } catch (err) {
        console.error('Ошибка при получении постбэков для кампании:', err);
        error.value = 'Ошибка при получении постбэков для кампании';
      } finally {
        loading.value = false;
      }
    };
    
    // Просмотр деталей кампании
    const viewCampaignDetails = (campaignId) => {
      selectedCampaign.value = campaignId;
      fetchCampaignPostbacks(campaignId);
    };
    
    // Форматирование даты
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };
    
    // Форматирование валюты
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(amount);
    };
    
    // Получение типа события
    const getEventType = (postback) => {
      if (postback.reg) return 'Регистрация';
      if (postback.conf) return 'Подтверждение';
      if (postback.ftd) return 'Первый депозит';
      if (postback.dep) return 'Депозит';
      return 'Неизвестно';
    };
    
    // Обработчик нового постбэка
    const handleNewPostback = (postback) => {
      console.log('Получен новый постбэк:', postback);
      
      // Обновляем список всех постбэков
      allPostbacks.value.unshift(postback);
      
      // Если открыта кампания, к которой относится постбэк, добавляем его в список
      if (selectedCampaign.value && postback.ac === selectedCampaign.value) {
        campaignPostbacks.value.unshift(postback);
      }
      
      // Запрашиваем обновление статистики
      socket.emit('request_postback_stats');
    };
    
    // Обработчик обновления статистики
    const handleStatsUpdate = (updatedStats) => {
      console.log('Получено обновление статистики постбэков:', updatedStats);
      stats.value = updatedStats;
    };
    
    // Подписка на WebSocket события
    onMounted(() => {
      fetchStats();
      
      // Запрашиваем обновление статистики через WebSocket
      socket.emit('request_postback_stats');
      
      // Подписываемся на обновления
      socket.on('new_postback', handleNewPostback);
      socket.on('postback_stats_updated', handleStatsUpdate);
    });
    
    // Отписка от WebSocket событий при размонтировании
    onUnmounted(() => {
      socket.off('new_postback', handleNewPostback);
      socket.off('postback_stats_updated', handleStatsUpdate);
    });
    
    // Следим за изменением выбранной кампании
    watch(selectedCampaign, (newValue) => {
      if (newValue) {
        fetchCampaignPostbacks(newValue);
      } else {
        campaignPostbacks.value = [];
      }
    });
    
    return {
      stats,
      filteredStats,
      campaignPostbacks,
      filteredCampaignPostbacks,
      selectedCampaign,
      loading,
      error,
      dateRange,
      startDate,
      endDate,
      postbackUrl,
      viewCampaignDetails,
      copyPostbackUrl,
      formatDate,
      formatCurrency,
      getEventType,
      applyDateFilter
    };
  }
};
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

h1 {
  margin-bottom: 20px;
  color: #333;
}

.date-filter {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 15px;
}

.custom-date-range {
  display: flex;
  gap: 20px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-group label {
  font-weight: bold;
  color: #555;
}

.filter-group select,
.filter-group input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.postback-url-section {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 30px;
}

.postback-url-container {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.postback-url-input {
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: #f9f9f9;
}

.copy-button {
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0 15px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.copy-button:hover {
  background-color: #388e3c;
}

.postback-info {
  font-size: 14px;
  color: #666;
}

.postback-info p {
  margin: 5px 0;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stats-card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  text-align: center;
}

.stats-card-title {
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
}

.stats-card-value {
  font-size: 24px;
  font-weight: bold;
  color: #2196f3;
}

.campaigns-section {
  margin-bottom: 30px;
}

.campaigns-table-container {
  overflow-x: auto;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.campaigns-table {
  width: 100%;
  border-collapse: collapse;
}

.campaigns-table th,
.campaigns-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.campaigns-table th {
  background-color: #f5f5f5;
  font-weight: bold;
  color: #555;
}

.view-details-button {
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.view-details-button i {
  margin-right: 5px;
}

.view-details-button:hover {
  background-color: #1976d2;
}

.campaign-details {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 30px;
}

.campaign-details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.close-button {
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.close-button:hover {
  background-color: #d32f2f;
}

.postbacks-table-container {
  overflow-x: auto;
}

.postbacks-table {
  width: 100%;
  border-collapse: collapse;
}

.postbacks-table th,
.postbacks-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.postbacks-table th {
  background-color: #f5f5f5;
  font-weight: bold;
  color: #555;
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
  
  .date-filter,
  .custom-date-range {
    flex-direction: column;
  }
}
</style> 